import MetallicPillButton from '@/components/Base/MetallicPillButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import { SurveyBrownPanel, SurveyBrownPanelInset } from '@/components/layout/SurveyBrownPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { useAppSelector } from '@/stores/hooks';
import {
  selectPdpaConsent,
  selectSurveyScreening,
  selectUniqueCodeDraft,
} from '@/stores/selectors';
import { readSmokerProfileAnswers, writeSmokerProfileAnswers } from '@/lib/smokerProfileStorage';
import { isValidEmail } from '@/utils/email';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const RACES = ['Malay', 'Chinese', 'Indian', 'Others'] as const;

function raceOptionLabel(
  race: (typeof RACES)[number],
  labels: {
    raceMalay: string;
    raceChinese: string;
    raceIndian: string;
    raceOthers: string;
  }
): string {
  switch (race) {
    case 'Malay':
      return labels.raceMalay;
    case 'Chinese':
      return labels.raceChinese;
    case 'Indian':
      return labels.raceIndian;
    case 'Others':
      return labels.raceOthers;
    default:
      return race;
  }
}

const MY_STATES = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Kuala Lumpur',
  'Melaka',
  'Negeri Sembilan',
  'Pahang',
  'Perak',
  'Perlis',
  'Pulau Pinang',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
] as const;

const selectClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300 disabled:cursor-not-allowed disabled:opacity-70';

const lightInputClassName =
  'min-w-0 w-full rounded-sm border border-slate-300/90 bg-white px-3 py-2.5 text-[16px] font-semibold text-slate-900 placeholder:font-normal placeholder:text-slate-400 outline-none ring-0 focus:border-slate-400 focus:ring-1 focus:ring-slate-300';

const titleClass =
  'm-0 mb-3 text-left text-[16px] font-medium text-white/95 sm:text-[17px]';

export default function SmokerProfile() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { mobileLocalDigits, customerUuid } = useAppSelector(selectUniqueCodeDraft);

  const [name, setName] = useState(() => readSmokerProfileAnswers()?.name ?? '');
  const [email, setEmail] = useState(() => readSmokerProfileAnswers()?.email ?? '');
  const [race, setRace] = useState(() => readSmokerProfileAnswers()?.race ?? '');
  const [state, setState] = useState(() => readSmokerProfileAnswers()?.state ?? '');
  const [nextWarning, setNextWarning] = useState<string | null>(null);

  useEffect(() => {
    writeSmokerProfileAnswers({
      name,
      email,
      race,
      state,
      brand: '',
      variant: '',
    });
  }, [name, email, race, state]);

  useEffect(() => {
    setNextWarning(null);
  }, [name, email, race, state]);

  const handleNext = () => {
    const n = name.trim();
    const e = email.trim();
    const r = race.trim();
    const s = state.trim();
    if (!n || !r || !s) {
      setNextWarning(t.smokerProfile.incompleteWarning);
      return;
    }
    if (!isValidEmail(e)) {
      setNextWarning(t.smokerProfile.emailWarning);
      return;
    }
    writeSmokerProfileAnswers({ name: n, email: e, race: r, state: s, brand: '', variant: '' });
    setNextWarning(null);
    navigate('/otherbrands');
  };

  if (surveyLanguage == null) {
    return <Navigate to="/" replace />;
  }
  if (surveyScreening == null) {
    return <Navigate to="/age_smoker" replace />;
  }
  if (!pdpaConsent) {
    return <Navigate to="/pdpa" replace />;
  }
  if (!mobileLocalDigits || mobileLocalDigits.replace(/\D/g, '').length === 0) {
    return <Navigate to="/uniquecode" replace />;
  }
  if (!customerUuid) {
    return <Navigate to="/uniquecode" replace />;
  }
  // Both LBS and Dice use this page; brand BG comes from SurveyBackgroundLayout.

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex min-h-0 flex-1 flex-col justify-center overflow-hidden">
        <div className="mx-auto flex w-full max-w-full flex-col py-6">
          <SurveyBrownPanelInset>
            <p className={titleClass}>{t.smokerProfile.nameLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <input
                id="smoker-name"
                type="text"
                name="name"
                autoComplete="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={lightInputClassName}
                placeholder={t.smokerProfile.namePlaceholder}
              />
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <p className={titleClass}>{t.smokerProfile.emailLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <input
                id="smoker-email"
                type="email"
                name="email"
                autoComplete="email"
                inputMode="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={lightInputClassName}
                placeholder={t.smokerProfile.emailPlaceholder}
              />
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <p className={titleClass}>{t.smokerProfile.raceLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <select
                id="smoker-race"
                name="race"
                value={race}
                onChange={(e) => setRace(e.target.value)}
                className={selectClassName}
              >
                <option value="">{t.smokerProfile.selectRace}</option>
                {RACES.map((r) => (
                  <option key={r} value={r}>
                    {raceOptionLabel(r, t.smokerProfile)}
                  </option>
                ))}
              </select>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-6">
            <p className={titleClass}>{t.smokerProfile.stateLabel}</p>
            <SurveyBrownPanel className="!p-4">
              <select
                id="smoker-state"
                name="state"
                value={state}
                onChange={(e) => setState(e.target.value)}
                className={selectClassName}
              >
                <option value="">{t.smokerProfile.selectState}</option>
                {MY_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </SurveyBrownPanel>
          </SurveyBrownPanelInset>

          <SurveyBrownPanelInset className="mt-10">
            <div className="flex flex-col items-center gap-3">
              {nextWarning != null ? <SurveyValidationAlert>{nextWarning}</SurveyValidationAlert> : null}
              <MetallicPillButton
                type="button"
                size="sm"
                className="metallic-pill--lang-home"
                onClick={handleNext}
              >
                {t.smokerProfile.continue}
              </MetallicPillButton>
            </div>
          </SurveyBrownPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
